import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { SignalIndicator } from '~/components';
import * as lib from '../../dependencies';
import { Label, Metrics, Title, Value } from './styles';

const SAMPLE_INTERVAL = 2000;

const EMPTY_METRICS = {
  rtt: null,
  jitter: null,
  packetLoss: null,
  codec: null,
  mos: null,
};

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    padding: '8px 10px',
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
  arrow: {
    color: '#f5f5f9',
  },
}))(Tooltip);

function getMOS(latency, jitter, packetLost) {
  const effective_lantency = latency + jitter * 2 + 10;
  let R;
  let MOS;

  if (effective_lantency < 160) {
    R = 93.2 - effective_lantency / 40;
  } else {
    R = 93.2 - (effective_lantency - 120) / 10;
  }

  R -= 2.5 * packetLost;

  if (R < 0) {
    MOS = 1.0;
  } else if (R >= 0 && R < 100) {
    MOS = 1 + 0.035 * R + 0.000007 * R * (R - 60) * (100 - R);
  } else {
    MOS = 4.5;
  }

  return MOS;
}

const isAudio = report => (report.kind || report.mediaType) === 'audio';

const toNumber = value => (Number.isFinite(value) ? value : null);

/**
 * @description Flatten an `RTCStatsReport` into the handful of numbers the tooltip shows.
 *
 * `remote-inbound-rtp` is the far end's view of what we sent, so it is the only source that
 * describes the quality the other party actually hears - but it is derived from RTCP receiver
 * reports, which only start arriving a few seconds into the call. Until then we fall back to the
 * selected candidate pair (RTT) and our own `inbound-rtp` (jitter/loss) so the panel is populated
 * from the first sample instead of sitting on dashes.
 */
function readAudioMetrics(statsReport) {
  const reports = [];
  statsReport.forEach(report => reports.push(report));

  const outbound = reports.find(r => r.type === 'outbound-rtp' && isAudio(r));
  const inbound = reports.find(r => r.type === 'inbound-rtp' && isAudio(r));
  const remoteInbound =
    reports.find(
      r => r.type === 'remote-inbound-rtp' && r.id === outbound?.remoteId
    ) || reports.find(r => r.type === 'remote-inbound-rtp' && isAudio(r));
  const candidatePair =
    reports.find(
      r => r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded'
    ) ||
    reports.find(r => r.type === 'candidate-pair' && r.state === 'succeeded');
  const codec =
    reports.find(r => r.type === 'codec' && r.id === outbound?.codecId) ||
    reports.find(r => r.type === 'codec' && r.mimeType?.startsWith('audio/'));

  const rtt =
    toNumber(remoteInbound?.roundTripTime) ??
    toNumber(candidatePair?.currentRoundTripTime);
  const jitter = toNumber(remoteInbound?.jitter) ?? toNumber(inbound?.jitter);

  // `packetsLost` is cumulative and can go negative when duplicates arrive, hence the clamp.
  let packetLoss = null;
  if (
    Number.isFinite(remoteInbound?.packetsLost) &&
    Number.isFinite(outbound?.packetsSent) &&
    outbound.packetsSent > 0
  ) {
    packetLoss = Math.max(
      0,
      (remoteInbound.packetsLost * 100) / outbound.packetsSent
    );
  } else if (
    Number.isFinite(inbound?.packetsLost) &&
    Number.isFinite(inbound?.packetsReceived)
  ) {
    const total = inbound.packetsLost + inbound.packetsReceived;
    packetLoss =
      total > 0 ? Math.max(0, (inbound.packetsLost * 100) / total) : null;
  }

  const codecName = codec?.mimeType?.split('/')[1] || null;

  const metrics = {
    rtt: rtt === null ? null : rtt * 1000,
    jitter: jitter === null ? null : jitter * 1000,
    packetLoss,
    codec: codecName && codecName.charAt(0).toUpperCase() + codecName.slice(1),
    mos: null,
  };

  if (metrics.rtt !== null && metrics.jitter !== null && packetLoss !== null) {
    metrics.mos = getMOS(metrics.rtt, metrics.jitter, packetLoss);
  }

  return metrics;
}

const formatMs = value =>
  Number.isFinite(value) ? `${value.toFixed(1)} ms` : '-';

const formatPercent = value =>
  Number.isFinite(value) ? `${value.toFixed(2)}%` : '-';

export default function RTCStats({ callId }) {
  // SELECTORS
  const calls = useSelector(state => state.callCurrent);
  const callsMemo = useMemo(() => (calls && calls.data) || [], [calls]);

  // STATES
  const [metrics, setMetrics] = useState(EMPTY_METRICS);

  // EFFECTS
  useEffect(() => {
    const call = callsMemo.find(e => e.id === callId);

    if (!callId || !call) {
      return undefined;
    }

    let cancelled = false;

    const sample = async () => {
      const pc = call.session?.sessionDescriptionHandler?.peerConnection;

      if (!call.answered || !pc || pc.signalingState === 'closed') {
        return;
      }

      try {
        // The whole connection, not `getSenders()[0].getStats()`: a sender-scoped report drops
        // the candidate pair and the inbound stream, which are exactly the fallbacks above.
        const next = readAudioMetrics(await pc.getStats());

        if (!cancelled) {
          setMetrics(next);
        }
      } catch (error) {
        // A peer connection closing mid-sample rejects getStats(); the next tick recovers.
      }
    };

    sample();
    const interval = setInterval(sample, SAMPLE_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [callId, callsMemo]);

  return (
    <div>
      <HtmlTooltip
        title={
          <Metrics>
            <Title>Call metrics</Title>
            <Label>Latency:</Label>
            <Value>{formatMs(metrics.rtt)}</Value>
            <Label>Jitter:</Label>
            <Value>{formatMs(metrics.jitter)}</Value>
            <Label>Packet loss:</Label>
            <Value>{formatPercent(metrics.packetLoss)}</Value>
            <Label>Codec:</Label>
            <Value>{metrics.codec || '-'}</Value>
            <Label>MOS:</Label>
            <Value>
              {Number.isFinite(metrics.mos) ? metrics.mos.toFixed(2) : '-'}
            </Value>
          </Metrics>
        }
        arrow>
        <div>
          <SignalIndicator
            score={Number.isFinite(metrics.mos) ? metrics.mos : 0}
          />
        </div>
      </HtmlTooltip>
    </div>
  );
}

RTCStats.propTypes = {
  callId: lib.PropTypes.string.isRequired,
};
